import React, {useEffect, useRef, useState} from "react";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {classNames} from "primereact/utils";
import {InputSwitch} from "primereact/inputswitch";
import {deleteQueueBranch, setQueueACDReplace, setQueueField, setQueueTrankIds} from "../../../redux/actions/actionsQueue";
import {Button} from "primereact/button";
import {PROD} from "../../../redux/config";
import {getUsersManagerUserBranchesData} from "../../../redux/api/apiUsersList";
import {MultiSelect} from "primereact/multiselect";
import {addQualificationGroupBranch} from "../../../redux/actions/actionsQualificationGroup";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import {getBranchDetailById} from "../../../redux/api/apiBranchList";
import {setEditType} from "../../../redux/actions/actions";
import {setBranchDialogVisible} from "../../../redux/actions/actionsBranchList";
import {getTrunkById} from "../../../redux/api/apiTrunks";
import {setTrunkDialogVisible} from "../../../redux/actions/actionsTrunk";
import TrunkListDialog from "../../Settings/TrunksList/TrunkDialog";

const QueuesListDialogGeneralSettings = () => {
    const dispatch = useAppDispatch();
    const queueData = useAppSelector(state => state.QueueListReducer.queueData);
    const errorFields = useAppSelector(state => state.QueueListReducer.errorFields);
    const companiesDirectoryTranksPhones = useAppSelector(state => state.QueueListReducer.queuesDirectoryTrunksPhone);
    const phoneField = useRef(null);
    const nameField = useRef(null);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const archiveTableToggle = useAppSelector(state => state.QueueListReducer.archiveQueueTableToggle)
    // @ts-ignore
    const queueTrunkIds = useAppSelector(state => state.QueueListReducer.queueTrunkIds);
    const [selectedPairs, setSelectedPairs] = useState<any>([{selectedGateway: null, selectedPhone: null}]);
    const branches = useAppSelector(state => state.UsersListReducer.branches);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const selectedCompanyId = useAppSelector(state => state.QualificationGroupListReducer.selectedQualificationGroupId);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const branchPermissions = checkPermissions(rolesVision, 'Branch');
    const branchDetailsLoader = useAppSelector(state => state.BranchesListReducer.branchDetailsLoader);
    const queuesTypeDictionary = useAppSelector(state => state.QueueListReducer.queuesDirectoryDictionary700);
    const [currentUserId, setCurrentUserId] = useState<any>(null);
    const detailsLoading = useAppSelector(state => state.TrunksListReducer.detailsLoading) as unknown as Record<string, string[]>;
    const [id, setId] = useState<any>(false);
    const trunkDialogVisible = useAppSelector(state => state.TrunksListReducer.trunkDialogVisible);
    const rolePermissions = checkPermissions(rolesVision, 'Trunks');
    const companyStateSave = useAppSelector(state => state.CompaniesListReducer.companyStateSave);
    const queueDetailsLoading = useAppSelector(state => state.QueueListReducer.detailsLoading);
    const branchIsBlock = queueData?.permission?.branchIsBlock;
    const trunksIsBlock = !!queueData?.permission?.trunksIsBlock;
    const disabledInputClass = classNames('w-full', {'opacity-100': archiveTableToggle && editType});
    const showTrunksReadOnly = trunksIsBlock;

    const trunksDisplayPairs = showTrunksReadOnly && queueData?.queueTrunks?.length
        ? queueData.queueTrunks.map((queueTrunk: any) => ({
            selectedGateway: queueTrunk.trunkId ?? null,
            selectedPhone: queueTrunk.trunkPhoneId ?? null,
            trunkName: queueTrunk.trunkName,
            phoneNo: queueTrunk.phoneNo,
        }))
        : selectedPairs;
    const trunksPhonesWithPayload = (() => {
        const dict = Array.isArray(companiesDirectoryTranksPhones)
            ? companiesDirectoryTranksPhones
            : [];
        const base = dict.map((item: any) => ({
            ...item,
            sofiaTrunkId: item.sofiaTrunkId ?? item.trunkId ?? item.id,
            profileName: item.profileName ?? item.trunkName ?? item.name,
            phoneNo: item.phoneNo,
            id: item.id ?? item.trunkPhoneId,
        }));
        (queueData?.queueTrunks || []).forEach((queueTrunk: any) => {
            const trunkId = queueTrunk.trunkId;
            const phoneId = queueTrunk.trunkPhoneId;
            if (phoneId == null) {
                return;
            }
            const hasPhone = base.some((item: any) => String(item.id) === String(phoneId));
            if (trunkId != null && !hasPhone) {
                base.push({
                    id: phoneId,
                    sofiaTrunkId: trunkId,
                    profileName: queueTrunk.trunkName,
                    phoneNo: queueTrunk.phoneNo,
                });
            }
        });
        return base;
    })();

    const {
        visible: hasVisible,
        update: hasUpdatePermission
    } = rolePermissions;

    const {
        visible: hasBranchVisionPermission,
        update: hasBranchUpdatePermission
    } = branchPermissions;

    const canOpenTrunkDetails = (trunkId: any) =>
        Boolean(
            !trunksIsBlock &&
            hasVisible &&
            trunkId != null &&
            (Array.isArray(companiesDirectoryTranksPhones) ? companiesDirectoryTranksPhones : []).some(
                (item: any) => String(item.sofiaTrunkId ?? item.trunkId) === String(trunkId)
            )
        );

    const trunkDetailsIcon = archiveTableToggle && hasUpdatePermission ? 'pi pi-pencil' : 'pi pi-eye';

    const addPair = (selectedGateway: null, selectedPhone: null) => {
        setSelectedPairs((prevPairs: any[]) => {
            if (selectedGateway != null && selectedPhone != null) {
                const pairExists = prevPairs.some(pair => pair.selectedGateway === selectedGateway && pair.selectedPhone === selectedPhone);
                if (pairExists) {
                    return prevPairs;
                }
            }
            return [...prevPairs, {selectedGateway, selectedPhone}];
        });
    };

    useEffect(() => {
        if (branchIsBlock) {
            return;
        }
        if (!companyStateSave && queueDetailsLoading) {
            return;
        }
        dispatch(getUsersManagerUserBranchesData(jwtToken?.jwtToken));
    }, [branchIsBlock, companyStateSave, queueDetailsLoading, jwtToken?.jwtToken]);

    useEffect(() => {
        dispatch(setBranchDialogVisible(false));
    }, []);


    const removePair = (indexToRemove: number) => {
        setSelectedPairs((currentPairs: any[]) =>
            currentPairs.filter((_, index) => index !== indexToRemove)
        );
    };

    const changeSelectedGateway = (value: any, index: number) => {
        setSelectedPairs((prevPairs: any[]) =>
            prevPairs.map((pair, idx) =>
                idx === index ? {...pair, selectedGateway: value, selectedPhone: null} : pair
            )
        );
    };

    const changeSelectedPhone = (value: null, index: any) => {
        setSelectedPairs((prevPairs: any[]) =>
            prevPairs.map((pair, idx) =>
                idx === index ? { ...pair, selectedPhone: value } : pair
            )
        );
    };
    const scrollIntoNameView = () => {
        // @ts-ignore
        nameField?.current?.scrollIntoView({behavior: 'smooth'});
    }

    const scrollIntoTrankView = () => {
        // @ts-ignore
        phoneField?.current?.scrollIntoView({behavior: 'smooth'});
    }
    const priorityOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    useEffect(() => {
        if (!queueData.queue.name && errorFields?.name) {
            scrollIntoNameView();
        }
        if (errorFields?.nameUniq) {
            scrollIntoNameView();
        }
        if (errorFields?.trankId && queueData.trankId) {
            scrollIntoTrankView();
        }
    }, [errorFields, queueData]);


    const formattedBranches = branches?.map((branch: any) => ({
        ...branch,
        label: `${branch.id} ${branch.name}`,
    }));

    useEffect(() => {
        if (!queueData?.queueTrunks?.length) {
            if (queueData?.queueTrunks?.length === 0) {
                setSelectedPairs(trunksIsBlock ? [] : [{selectedGateway: null, selectedPhone: null}]);
            }
            return;
        }

        const restoredPairs = queueData.queueTrunks.map((queueTrunk: any) => {
            const isEntireGroup = queueTrunk.trunkPhoneId == null;
            const foundByPhone = !isEntireGroup
                ? trunksPhonesWithPayload?.find(
                    (item: any) => String(item.id) === String(queueTrunk.trunkPhoneId)
                )
                : undefined;
            const foundByTrunkAndPhone = !isEntireGroup
                ? trunksPhonesWithPayload?.find(
                    (item: any) =>
                        String(item.sofiaTrunkId) === String(queueTrunk.trunkId) &&
                        (String(item.id) === String(queueTrunk.trunkPhoneId) ||
                          String(item.phoneNo) === String(queueTrunk.phoneNo))
                )
                : undefined;
            const foundByTrunk = trunksPhonesWithPayload?.find(
                (item: any) => String(item.sofiaTrunkId) === String(queueTrunk.trunkId)
            );
            const found = foundByPhone || foundByTrunkAndPhone || foundByTrunk;

            return {
                selectedGateway: queueTrunk.trunkId ?? found?.sofiaTrunkId ?? null,
                selectedPhone: isEntireGroup ? null : (queueTrunk.trunkPhoneId ?? found?.id ?? null),
                trunkName: queueTrunk.trunkName || found?.profileName,
                phoneNo: isEntireGroup ? null : (queueTrunk.phoneNo || found?.phoneNo),
            };
        });

        setSelectedPairs(restoredPairs);
    }, [companiesDirectoryTranksPhones, queueData?.queueTrunks, trunksIsBlock, queueData?.queue?.id]);

    useEffect(() => {
        if (trunksIsBlock) {
            return;
        }
        if (queueTrunkIds?.length > 0 && selectedPairs.length === 0) {
            const restoredPairs = queueTrunkIds.map((trunk: any) => ({
                selectedGateway: trunk.trunkId ?? null,
                selectedPhone: trunk.trunkPhoneId ?? null
            }));
            setSelectedPairs(restoredPairs);
        }
    }, [queueTrunkIds, trunksIsBlock]);

    const onChangeNameField = (e: any) => {
        dispatch(setQueueField({section: 'queue', fieldName: 'name', value: e.target.value}));
    }

    const changeQueueBranches = (e: any) => {
        const filteredEValue = e.value?.filter((item: { id: number; }) => item.id);
        const existingIds = new Set(filteredEValue.map((item: { id: number; }) => item.id));
        const skillGroupData = new Set(formattedBranches.map((item: { id: number; }) => item.id));
        const newItems = queueData && queueData.queueBranches
            ? queueData.queueBranches.filter((item: { id: number; }) =>
                !existingIds.has(item.id) && !skillGroupData.has(item.id))
            : [];
        const updatedEValue = [...filteredEValue, ...newItems];

        dispatch(addQualificationGroupBranch({ campaignId: selectedCompanyId, id: updatedEValue }));
    }

    const branchesValue = branchIsBlock
        ? (queueData?.queueBranches || []).map((branch: any) => ({
            ...branch,
            label: branch.name ? `${branch.id} ${branch.name}` : `${branch.id}`,
        }))
        : queueData?.queueBranches?.map((companiesItem: any) => {
        const user = formattedBranches.find((userItem: any) => userItem.id === companiesItem.id);
        const {queueId, isMainIntegrationUrl, ...userWithoutCampaignId} = companiesItem;
        return {
            ...userWithoutCampaignId,
            description: user?.description,
            id: user?.id ?? companiesItem.id,
            isArchived: user?.isArchived,
            name: user?.name || companiesItem.name,
            label: user?.label || (companiesItem.name ? `${companiesItem.id} ${companiesItem.name}` : `${companiesItem.id}`),
            ownerId: user?.ownerId,
            ownerName: user?.ownerName,
            parentId: user?.parentId,
            parentName: user?.parentName,
            userCount: user?.userCount,
            integrationURLId: user?.integrationURLId,
            isMainIntegrationUrl: user?.isMainIntegrationUrl,
            mainBranchId: user?.mainBranchId
        }
    });

    const branchItems = branchIsBlock
        ? queueData?.queueBranches?.map((branchItem: any, index: number) => (
            <div key={branchItem.id || index} className="flex align-items-center mb-2">
                <div className="field col-10 md:col-10 flex align-items-center m-0">
                    {branchItem.name ? `${branchItem.id} ${branchItem.name}` : branchItem.id}
                </div>
            </div>
        ))
        : queueData?.queueBranches?.map((queueItem: any) => {
        return formattedBranches?.map((branchItem: any, index: number) => {
            if (queueItem.id === branchItem.id) {
                return (
                    <div key={index} className="flex align-items-center mb-2">
                        <div className="field col-10 md:col-10 flex align-items-center m-0">
                            {hasBranchVisionPermission && <Button
                                className="max-w-4rem p-button-secondary mr-3"
                                style={{minWidth: '40px'}}
                                loading={branchDetailsLoader && currentUserId === branchItem.id}
                                disabled={!archiveTableToggle || branchDetailsLoader}
                                icon={archiveTableToggle && hasBranchUpdatePermission ? 'pi pi-pencil' : !archiveTableToggle || !hasBranchUpdatePermission ? 'pi pi-eye' : ''}
                                onClick={() => {
                                    setCurrentUserId(branchItem.id);
                                    dispatch(getBranchDetailById(branchItem.id, jwtToken?.jwtToken, true));
                                    if (hasBranchUpdatePermission) {
                                        dispatch(setEditType(true));
                                    } else {
                                        dispatch(setEditType(false));
                                    }
                                }}/>}
                            {branchItem.label}
                        </div>
                        <div className="field col-2 md:col-2 flex align-items-center justify-content-end m-0 p-0">
                            <Button name={branchItem.id}
                                    onClick={() => {
                                        dispatch(deleteQueueBranch(branchItem.id));
                                    }}
                                    icon="pi pi-times"
                                    className="delete-user p-button-secondary p-button-rounded p-button-text field col-12 md:col-2 m-0"
                                    disabled={!archiveTableToggle || !editType}
                            />
                        </div>
                    </div>
                )
            }
        })
    });


    useEffect(() => {
        if (trunksIsBlock) {
            return;
        }
        const result = selectedPairs.map((pair: { selectedGateway: any; selectedPhone: any; }) => ({
            trunkId: pair.selectedGateway ?? null,
            trunkPhoneId: pair.selectedPhone ?? null
        }));
        dispatch(setQueueTrankIds(result));
    }, [selectedPairs, companiesDirectoryTranksPhones, trunksIsBlock]);

    const openEditor = (id: number) => {
        dispatch(getTrunkById(id, jwtToken?.jwtToken));
        dispatch(setTrunkDialogVisible(true));

        if (hasUpdatePermission) {
            dispatch(setEditType(true));
        } else {
            dispatch(setEditType(false));
        }
    };

    return (
        <div className="general-settings">
            {trunkDialogVisible && <TrunkListDialog/>}
            <div className="grid p-fluid">
                <div className="field col-12 md:col-6" ref={nameField}>
                    <label htmlFor="name" className={classNames('', {'p-error': errorFields?.name || errorFields?.nameUniq})}>Назва черги *</label>
                    <InputText
                        id="name"
                        value={queueData?.queue?.name || ''}
                        maxLength={150}
                        className={classNames('', {'p-invalid': errorFields?.name || errorFields?.nameUniq})}
                        onChange={e => {
                            onChangeNameField(e);
                        }}
                        disabled={!archiveTableToggle || !editType}
                    />
                    {errorFields?.nameUniq && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span className="title p-error text-sm">{errorFields?.nameUniq}*</span></div>}
                </div>
                <div className="field col-12 md:col-3">
                    <label htmlFor="priority">Пріоритет</label>
                    <Dropdown id="priority"
                              appendTo="self"
                              filter
                              value={queueData.queue.priority ?? 0}
                              options={priorityOptions}
                              onChange={e => {
                                  dispatch(setQueueField({section: 'queue', fieldName: 'priority', value: e.value}));
                              }}
                              disabled={!archiveTableToggle || !editType}
                              placeholder="Оберіть пріоритет"
                    />
                </div>
                <div className="field col-12 md:col-6">
                    <label htmlFor="description">Коментар</label>
                    <InputTextarea
                        id="description"
                        value={queueData?.queue?.description || ''}
                        onChange={e => {
                            dispatch(setQueueField({section: 'queue', fieldName: 'description', value: e.target.value}));
                        }}
                        autoResize
                        rows={3}
                        maxLength={500}
                        disabled={!archiveTableToggle || !editType}
                    />
                </div>
                <div className="field col-12 md:col-6"></div>
            </div>
            <div>
                <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>
                <div className="field col-12 md:col-4 flex flex-column">
                    <label htmlFor="type">Оберіть тип</label>
                        <Dropdown id="type"
                                  appendTo="self"
                                  filter
                                  value={queueData?.queue?.queueType || ''}
                                  options={queuesTypeDictionary}
                                  optionLabel="name"
                                  optionValue="dictionaryId"
                                  onChange={e => {
                                      dispatch(setQueueField({section: 'queue', fieldName: 'queueType', value: e.target.value}));
                                      if (e.target.value === 702) {
                                          dispatch(setQueueField({section: 'queue', fieldName: 'isQueueTrunk', value: true}));
                                          dispatch(setQueueACDReplace({
                                              isMWTNoAgentAudio: true,
                                              isSchedulerAudio: true,
                                              isMWTAudio: true,
                                              ivrConfiguration: [],
                                              isIVMAudio: false,
                                          }));
                                      }
                                  }}
                                  disabled={!archiveTableToggle || !editType}
                                  placeholder="Оберіть тип"
                        />
                </div>
                <div className="field col-12 md:col-4 flex flex-column">
                    <label htmlFor="name" className={classNames('', {'p-error': errorFields?.branchId && !queueData?.queue?.branchId && !queueData?.queueBranches?.length})}>Бранч *</label>
                    {branchIsBlock ? (
                        queueData?.queueBranches?.length ? (
                            queueData.queueBranches.map((branch: any) => (
                                <InputText
                                    key={branch.id}
                                    className={classNames(disabledInputClass, 'mb-2')}
                                    disabled
                                    value={branch.name ? `${branch.id} ${branch.name}` : String(branch.id ?? '')}
                                />
                            ))
                        ) : (
                            <InputText className={disabledInputClass} disabled value="" />
                        )
                    ) : (
                        <>
                            <MultiSelect
                                appendTo="self"
                                value={branchesValue}
                                options={formattedBranches}
                                className={classNames('', {'p-invalid': errorFields?.branchId})}
                                onChange={(e) => {
                                    changeQueueBranches(e);
                                }}
                                placeholder="Оберіть бранч"
                                filter
                                optionLabel="label"
                                selectedItemsLabel={branchesValue ? `Обрано: ${branchesValue?.length}` : ""}
                                maxSelectedLabels={0}
                                scrollHeight="250px"
                                disabled={!archiveTableToggle || !editType}
                            />
                            {
                                branchesValue?.length ?
                                    <div className="users-list mt-3">
                                        {branchItems}
                                    </div>
                                    :
                                    <>
                                        {
                                            errorFields?.branchId ? <span className={classNames('', {'block title p-error text-sm mt-4': errorFields?.branchId})}>Нічого не вибрано</span> :
                                                <span className={classNames('no-users-message mt-4')}>Нічого не вибрано</span>
                                        }
                                    </>
                            }
                        </>
                    )}
                </div>
                <div>
                    {!PROD && <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>}
                    <label htmlFor="name">Стан</label>
                    <div className="field-radiobutton my-4 pl-2">
                        <label htmlFor="name" className="mr-2 w-6rem">Не активна</label>
                        <InputSwitch
                            disabled={!archiveTableToggle || !editType}
                            className="mr-3"
                            checked={queueData.queue.isActive} onChange={(e) => {
                            dispatch(setQueueField({section: 'queue', fieldName: 'isActive', value: e.value}));
                        }}/>
                        <label htmlFor="icon">Активна</label>
                    </div>
                    <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>
                    {
                        // !PROD &&
                        <h6>Транк</h6>}
                    {
                        // !PROD &&
                        <div className="field-radiobutton my-4 pl-2">
                            <label htmlFor="name" className="mr-2">Транк Оператора</label>
                            <InputSwitch
                                disabled={!archiveTableToggle || !editType || queueData?.queue?.queueType === 702 || trunksIsBlock}
                                className="mx-3"
                                value={queueData.queue.isQueueTrunk}
                                checked={queueData.queue.isQueueTrunk}
                                onChange={(e) => {
                                    if (e.value) {
                                        dispatch(setQueueField({section: 'queue', fieldName: 'isQueueTrunk', value: e.value}));
                                    } else {
                                        dispatch(setQueueField({section: 'queue', fieldName: 'isQueueTrunk', value: e.value}));
                                    }
                            }}/>
                            <label htmlFor="icon">Загальний Транк Черги</label>
                        </div>}
                    <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>

                    <div className="grid p-fluid">
                        <div className="grid col-12 md:col-6">
                            <div className="field col-12 md:col-5 mb-0">
                                <label className={classNames('', {'p-error': errorFields?.queueTranks || errorFields?.queueTranks})}>Транки*</label>
                            </div>
                            <div className="field col-12 md:col-5 mb-0">
                                <label className={classNames('', {'p-error': errorFields?.queueTranks || errorFields?.queueTranks})}>Номер*</label>
                            </div>
                            {trunksDisplayPairs?.map((pair: { selectedGateway: number | null; selectedPhone: any; trunkName?: string; phoneNo?: string }, index: number) => {
                                const usedTrunkIdsWithoutPhone = new Set(
                                    trunksDisplayPairs
                                        .filter((p: { selectedPhone: null; }, i: number) => p.selectedPhone === null && i !== index)
                                        .map((p: { selectedGateway: any; }) => String(p.selectedGateway))
                                );

                                const gatewayOptions = Array.from(
                                    new Map(
                                        (trunksPhonesWithPayload || [])
                                            .filter((item: any) =>
                                                !usedTrunkIdsWithoutPhone.has(String(item.sofiaTrunkId)) ||
                                                String(item.sofiaTrunkId) === String(pair.selectedGateway)
                                            )
                                            .map((item: any) => [
                                                item.sofiaTrunkId,
                                                item.profileName ||
                                                    (String(item.sofiaTrunkId) === String(pair.selectedGateway) ? pair.trunkName : null) ||
                                                    item.sofiaTrunkId,
                                            ])
                                    ).entries()
                                ).map(([value, label]) => ({ value, label }));

                                if (
                                    pair.selectedGateway != null &&
                                    !gatewayOptions.some((g) => String(g.value) === String(pair.selectedGateway))
                                ) {
                                    gatewayOptions.push({
                                        value: pair.selectedGateway,
                                        label: pair.trunkName || pair.selectedGateway,
                                    });
                                }

                                const phoneOptions = (trunksPhonesWithPayload || []).filter((item: any) =>
                                    String(item.sofiaTrunkId) === String(pair.selectedGateway) &&
                                    (
                                        !trunksDisplayPairs.some((selectedPair: { selectedPhone: any; }, selectedIndex: number) =>
                                            selectedIndex !== index && String(selectedPair.selectedPhone) === String(item.id)
                                        ) || String(pair.selectedPhone) === String(item.id)
                                    )
                                );
                                if (
                                    pair.selectedPhone != null &&
                                    !phoneOptions.some((item: any) => String(item.id) === String(pair.selectedPhone))
                                ) {
                                    phoneOptions.push({
                                        id: pair.selectedPhone,
                                        sofiaTrunkId: pair.selectedGateway,
                                        profileName: pair.trunkName,
                                        phoneNo: pair.phoneNo || pair.selectedPhone,
                                    });
                                }

                                const isLoading = id === pair.selectedGateway && Boolean(detailsLoading);
                                const isDisabled = Boolean(id) && Boolean(detailsLoading);
                                const showTrunkDetailsButton = canOpenTrunkDetails(pair.selectedGateway);

                                const handleShowDetails = () => {
                                    if (!pair.selectedGateway) return;
                                    openEditor(pair.selectedGateway);
                                    setId(pair.selectedGateway);
                                };

                                const hasGatewayError = errorFields?.queueTranks?.trunkIds?.includes(pair.selectedGateway);
                                const gatewayLabel = pair.trunkName
                                    || gatewayOptions.find((g) => String(g.value) === String(pair.selectedGateway))?.label
                                    || pair.selectedGateway
                                    || '';
                                const phoneLabel = pair.selectedPhone != null
                                    ? (pair.phoneNo
                                        || phoneOptions.find((item: any) => String(item.id) === String(pair.selectedPhone))?.phoneNo
                                        || pair.selectedPhone
                                        || '')
                                    : '';

                                return (
                                    <div className="grid col-12 md:col-12" key={index}>
                                        <div className="field col-12 md:col-5 mb-0">
                                            {showTrunksReadOnly ? (
                                                <InputText className={disabledInputClass} disabled value={String(gatewayLabel)} />
                                            ) : (
                                            <Dropdown
                                                filter
                                                emptyMessage="Немає доступних"
                                                value={pair.selectedGateway}
                                                options={gatewayOptions}
                                                optionValue="value"
                                                optionLabel="label"
                                                className={classNames({ 'p-invalid': hasGatewayError })}
                                                onChange={(e) => changeSelectedGateway(e.value, index)}
                                                disabled={!archiveTableToggle || !editType || !queueData?.queue?.isQueueTrunk}
                                                placeholder="Оберіть транк"
                                            />
                                            )}
                                        </div>

                                        <div className="field col-12 md:col-5 mb-0">
                                            {showTrunksReadOnly ? (
                                                <InputText className={disabledInputClass} disabled value={String(phoneLabel || '')} />
                                            ) : (
                                            <Dropdown
                                                filter
                                                value={pair.selectedPhone}
                                                optionLabel="phoneNo"
                                                emptyMessage="Немає доступних"
                                                optionValue="id"
                                                options={phoneOptions}
                                                onChange={(e) => changeSelectedPhone(e.value, index)}
                                                disabled={!pair.selectedGateway || !archiveTableToggle || !editType || !queueData?.queue?.isQueueTrunk}
                                                placeholder="Оберіть номер"
                                            />
                                            )}
                                        </div>

                                        {!showTrunksReadOnly && showTrunkDetailsButton && <div className="field col-12 md:col-1">
                                                <Button
                                                    disabled={isDisabled}
                                                    loading={isLoading}
                                                    onClick={handleShowDetails}
                                                    icon={trunkDetailsIcon}
                                                    style={{ minHeight: '30px', minWidth: '30px', maxWidth: '37px', marginLeft: '8px', maxHeight: '30px' }}
                                                    className={classNames('p-button-secondary flex align-items-center justify-content-center')}
                                                />
                                            </div>}

                                        {!showTrunksReadOnly && <div className="field col-12 md:col-1">
                                            {trunksDisplayPairs.length > 1 && (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-secondary"
                                                    disabled={!archiveTableToggle || !editType || trunksDisplayPairs.length < 1 || !queueData?.queue?.isQueueTrunk}
                                                    onClick={() => removePair(index)}
                                                />
                                            )}
                                        </div>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="grid col-12 md:col-6"></div>
                        <div className="grid col-12 md:col-6 pt-0">
                            {errorFields?.queueTranks && <div className="col-12 md:col-12 p-0 m-0 pl-2 mb-2"><span className="title p-error text-sm">{errorFields?.queueTranks?.queueTranks}*</span></div>}
                        </div>
                    </div>
                    {!showTrunksReadOnly && <Button disabled={!archiveTableToggle || !editType || !queueData?.queue?.isQueueTrunk} icon="pi pi-plus" onClick={() => addPair(null, null)}><label className="pl-2">Додати транк</label></Button>}
                </div>
            </div>
        </div>
    )
}

export default QueuesListDialogGeneralSettings
